import { FlatfileRecord } from '@flatfile/plugin-record-hook';
import { TENANT_TYPES, CHANNEL_TYPE_KEYS } from 'constants-channel';
import { SourceType } from 'graphql-query-contracts';
import { SourceTypeLabel } from '../../../../../../../type-maps/source-type-label.map';

const validSourceTypes = [
  SourceTypeLabel[SourceType.SourceTypeCbre],
  SourceTypeLabel[SourceType.SourceTypeMri],
  SourceTypeLabel[SourceType.SourceTypeSfdc],
  SourceTypeLabel[SourceType.SourceTypeVcm],
  SourceTypeLabel[SourceType.SourceTypeYardi],
];

const supportedTenantTypes = TENANT_TYPES.filter(
  type =>
    type !== CHANNEL_TYPE_KEYS.Professional &&
    type !== CHANNEL_TYPE_KEYS.Charity
);

const isSupportedTenantType = (value: unknown): boolean => {
  return (
    typeof value === 'string' &&
    (supportedTenantTypes as readonly string[]).includes(value)
  );
};

export const validateRecord = (record: FlatfileRecord) => {
  const name = record.get('name');
  const tenantType = record.get('tenantType');
  const suites = record.get('suites');
  const sourceType = record.get('sourceType');
  const externalLeaseId = record.get('externalLeaseId');
  const whiteListedEmailDomains = record.get('whiteListedEmailDomains');

  const parseCommaSeparated = (value: string | undefined): string[] => {
    if (!value) return [];

    return (value as string)
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
  };

  if (!name) {
    record.addError('name', 'Tenant name is required');
  }

  if (!tenantType) {
    record.addError('tenantType', 'Tenant type is required');
  } else if (!isSupportedTenantType(tenantType)) {
    record.addError('tenantType', 'Invalid tenant type');
  }

  const suitesArray = parseCommaSeparated(suites as string);
  const externalLeaseIds = parseCommaSeparated(externalLeaseId as string);
  const sourceTypes = parseCommaSeparated(sourceType as string);

  if (sourceTypes.length > 0) {
    for (const type of sourceTypes) {
      if (!validSourceTypes.includes(type)) {
        record.addError(
          'sourceType',
          `Invalid source type: ${type}. Valid values: ${validSourceTypes.join(', ')}`
        );
        break;
      }
    }
  }

  if (externalLeaseIds.length > 0) {
    if (suitesArray.length === 0) {
      record.addError('suites', 'suites is required');
    }
    // External Lease ID count must be equal to or less than Suite count
    else if (externalLeaseIds.length > suitesArray.length) {
      record.addError(
        'externalLeaseId',
        `Number of External Lease IDs (${externalLeaseIds.length}) must be equal to or less than number of Suites (${suitesArray.length})`
      );
    }

    // External Source Type is required when External Lease ID is provided
    if (sourceTypes.length === 0) {
      record.addError('sourceType', 'External Source Type is required');
    }
    // External Source Type count must equal External Lease ID count
    else if (sourceTypes.length !== externalLeaseIds.length) {
      record.addError(
        'sourceType',
        `Number of External Source Types (${sourceTypes.length}) must equal number of External Lease IDs (${externalLeaseIds.length})`
      );
    }
  }

  // Validate External Source Type can't be provided without External Lease ID
  if (sourceTypes.length > 0 && externalLeaseIds.length === 0) {
    record.addError(
      'externalLeaseId',
      'External Lease ID is required when External Source Type is provided'
    );
  }

  // Validate email domains format if provided
  if (whiteListedEmailDomains) {
    const domains = parseCommaSeparated(whiteListedEmailDomains as string);

    const domainRegex =
      /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/;

    for (const domain of domains) {
      if (!domainRegex.test(domain)) {
        record.addError(
          'whiteListedEmailDomains',
          `Invalid domain format: ${domain}`
        );
        break;
      }
    }
  }

  return record;
};
